import { useEffect, useRef, useState } from 'react';
import { Mic, MicOff } from 'lucide-react';
import { Button } from '../ui/button';
import logger from '../../utils/logger';

/**
 * Voice-search button using the Web Speech API.
 * Renders nothing if the browser doesn't support recognition.
 */
const VoiceSearchButton = ({ onTranscript, paletteOpen }) => {
  const [listening, setListening] = useState(false);
  const recognitionRef = useRef(null);

  const SR = typeof window !== 'undefined'
    ? (window.SpeechRecognition || window.webkitSpeechRecognition)
    : null;

  useEffect(() => {
    if (!paletteOpen && recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch { /* ignore */ }
      setListening(false);
    }
  }, [paletteOpen]);

  if (!SR) return null;

  const toggle = () => {
    if (listening) {
      try { recognitionRef.current?.stop(); } catch { /* ignore */ }
      setListening(false);
      return;
    }
    const recognition = new SR();
    recognition.lang = 'en-GB';
    recognition.interimResults = false;
    recognition.continuous = false;
    recognition.maxAlternatives = 1;
    recognition.onresult = (event) => {
      const transcript = event.results?.[0]?.[0]?.transcript || '';
      if (transcript) onTranscript(transcript);
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => setListening(false);
    recognitionRef.current = recognition;
    try {
      recognition.start();
      setListening(true);
    } catch (err) {
      logger.debug('voice search start failed', err);
      setListening(false);
    }
  };

  const Icon = listening ? MicOff : Mic;

  return (
    <Button
      size="icon"
      variant={listening ? 'default' : 'ghost'}
      className={`h-8 w-8 flex-shrink-0 ${listening ? 'bg-red-500 hover:bg-red-600 text-white animate-pulse' : ''}`}
      onClick={toggle}
      title={listening ? 'Stop listening' : 'Voice search'}
      data-testid="voice-search-toggle"
      type="button"
      aria-label={listening ? 'Stop voice search' : 'Start voice search'}
    >
      <Icon className="h-4 w-4" />
    </Button>
  );
};

export default VoiceSearchButton;
